import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import Seo, { breadcrumbLd, getSiteUrl } from "@/components/Seo";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

const SITE_URL = getSiteUrl();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", order: "", products: "", message: "" };

export default function WithdrawalPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(INITIAL);
  const [declaration, setDeclaration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {reference, received_at}
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const labelBase =
    "block text-[11px] uppercase tracking-[0.22em] mb-2 font-medium";
  const labelStyle = (err) =>
    `${labelBase} ${err ? "text-red-700" : "text-olive-800/70"}`;
  const inputBase =
    "w-full bg-cream-100 border px-4 py-3 text-olive-900 font-light focus:outline-none focus:border-olive-700";
  const inputStyle = (err) =>
    `${inputBase} ${err ? "border-red-700" : "border-cream-400"}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || result) return;
    const eMap = {};
    if (!form.name.trim()) eMap.name = true;
    if (!/^\S+@\S+\.\S+$/.test(form.email)) eMap.email = true;
    if (!form.order.trim()) eMap.order = true;
    if (!form.products.trim()) eMap.products = true;
    if (!declaration) eMap.declaration = true;
    setErrors(eMap);
    setSubmitError(null);
    if (Object.keys(eMap).length > 0) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/forms/withdrawal`, {
        name: form.name.trim(),
        email: form.email.trim(),
        order_number: form.order.trim(),
        products: form.products.trim(),
        message: (form.message || "").trim(),
        declaration: true,
        lang,
      });
      setResult({
        reference: res.data?.reference || "",
        received_at: res.data?.received_at || "",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setSubmitError(typeof detail === "string" ? detail : t("common.error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const canonical = `${SITE_URL}/desistimiento`;

  return (
    <main data-testid="withdrawal-page" className="min-h-screen bg-cream-100 text-charcoal">
      <Seo
        title={`${t("withdrawal.title")} | Cortijo Santa María`}
        description={t("withdrawal.intro")}
        canonical={canonical}
        lang={lang}
        noindex
        jsonLd={breadcrumbLd([
          { name: t("nav_ecom.home"), url: `${SITE_URL}/` },
          { name: t("withdrawal.title"), url: canonical },
        ])}
      />
      <ShopNav />
      <article className="max-w-3xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
          {t("withdrawal.overline")}
        </span>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl leading-tight text-olive-900">
          {t("withdrawal.title")}
        </h1>
        <p className="mt-6 text-olive-800/85 font-light leading-relaxed">
          {t("withdrawal.intro")}
        </p>

        {PRE_HARVEST_MODE && (
          <aside
            data-testid="withdrawal-preharvest-notice"
            role="note"
            className="mt-6 flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 px-5 py-4"
          >
            <AlertTriangle size={18} strokeWidth={1.4} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-900 font-light leading-relaxed">
              {lang === "es"
                ? "Actualmente no aceptamos pedidos ni realizamos envíos. La próxima cosecha está en preparación. Puedes unirte a la lista de espera desde la página de inicio."
                : "We are not currently accepting orders or shipping products. The next harvest is in preparation. You can join the waitlist from the home page."}
              {" "}
              <Link to="/" className="underline font-medium hover:text-amber-700">
                {lang === "es" ? "Ir al inicio →" : "Go to home →"}
              </Link>
            </p>
          </aside>
        )}

        {result ? (
          <div
            data-testid="withdrawal-success"
            role="status"
            className="mt-12 border border-olive-700/40 bg-cream-200 p-6 sm:p-8"
          >
            <div className="flex items-start gap-4">
              <CheckCircle2 className="text-olive-700 mt-1 shrink-0" size={22} strokeWidth={1.5} />
              <p className="text-olive-900 font-light leading-relaxed">{t("withdrawal.success")}</p>
            </div>
            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2 text-sm">
              <dt className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">
                {lang === "es" ? "Referencia" : "Reference"}
              </dt>
              <dd
                data-testid="withdrawal-reference"
                className="font-serif text-olive-900 tabular-nums"
              >
                {result.reference}
              </dd>
              <dt className="text-[10px] uppercase tracking-[0.24em] text-olive-800/60">
                {lang === "es" ? "Recibido el (UTC)" : "Received at (UTC)"}
              </dt>
              <dd
                data-testid="withdrawal-received-at"
                className="font-serif text-olive-900 tabular-nums"
              >
                {result.received_at}
              </dd>
            </dl>
          </div>
        ) : (
          <form
            data-testid="withdrawal-form"
            onSubmit={handleSubmit}
            noValidate
            className="mt-12 border border-cream-400 bg-cream-100 p-6 sm:p-8 space-y-6"
          >
            <h2 className="font-serif text-2xl text-olive-900">{t("withdrawal.form_title")}</h2>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelStyle(errors.name)}>{t("withdrawal.name_label")}</label>
                <input
                  data-testid="wd-name"
                  type="text"
                  className={inputStyle(errors.name)}
                  value={form.name}
                  onChange={set("name")}
                  required
                />
              </div>
              <div>
                <label className={labelStyle(errors.email)}>{t("withdrawal.email_label")}</label>
                <input
                  data-testid="wd-email"
                  type="email"
                  className={inputStyle(errors.email)}
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
            </div>

            <div>
              <label className={labelStyle(errors.order)}>{t("withdrawal.order_label")}</label>
              <input
                data-testid="wd-order"
                type="text"
                className={inputStyle(errors.order)}
                value={form.order}
                onChange={set("order")}
                required
              />
            </div>

            <div>
              <label className={labelStyle(errors.products)}>{t("withdrawal.products_label")}</label>
              <textarea
                data-testid="wd-products"
                rows={3}
                className={inputStyle(errors.products)}
                value={form.products}
                onChange={set("products")}
                required
              />
              <p className="mt-2 text-xs text-olive-800/60 font-light">{t("withdrawal.products_help")}</p>
            </div>

            <div>
              <label className={labelStyle(false)}>{t("withdrawal.message_label")}</label>
              <textarea
                data-testid="wd-message"
                rows={3}
                className={inputStyle(false)}
                value={form.message}
                onChange={set("message")}
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-olive-800/85 font-light cursor-pointer">
              <input
                data-testid="wd-declaration"
                type="checkbox"
                checked={declaration}
                onChange={(e) => setDeclaration(e.target.checked)}
                className="mt-1 accent-olive-700 w-4 h-4 shrink-0"
                required
              />
              <span className="leading-relaxed">{t("withdrawal.declaration")}</span>
            </label>

            <div className="pt-2">
              {submitError && (
                <p
                  data-testid="withdrawal-error"
                  role="alert"
                  className="mb-4 text-sm text-red-700 font-light"
                >
                  {submitError}
                </p>
              )}
              <button
                type="submit"
                data-testid="wd-submit"
                disabled={!declaration || submitting}
                aria-disabled={!declaration || submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 disabled:bg-olive-700/40 disabled:cursor-not-allowed disabled:hover:bg-olive-700/40"
              >
                {submitting ? t("checkout_page.processing") : t("withdrawal.submit")}
              </button>
            </div>
          </form>
        )}

        <section
          data-testid="withdrawal-info"
          className="mt-16 border-t border-cream-400 pt-10"
        >
          <h2 className="font-serif text-2xl text-olive-900">{t("withdrawal.info_title")}</h2>
          <ul className="mt-5 space-y-3 text-olive-800/85 font-light leading-relaxed list-disc pl-5">
            <li>{t("withdrawal.info_period")}</li>
            <li>{t("withdrawal.info_refund")}</li>
            <li>{t("withdrawal.info_aove")}</li>
            <li>{t("withdrawal.info_damaged")}</li>
          </ul>
        </section>

        <nav className="mt-16 border-t border-cream-400 pt-8 flex flex-wrap gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.24em] text-olive-800/70">
          <Link to="/legal/aviso" className="hover:text-olive-900">{t("legal.aviso_title")}</Link>
          <Link to="/legal/privacidad" className="hover:text-olive-900">{t("legal.privacy_title")}</Link>
          <Link to="/legal/devoluciones" className="hover:text-olive-900">{t("legal.returns_title")}</Link>
        </nav>

        <div className="mt-10">
          <Link to="/" className="text-[11px] uppercase tracking-[0.28em] text-olive-800 hover:text-olive-900">
            ← {t("legal.back_home")}
          </Link>
        </div>
      </article>
      <Footer />
    </main>
  );
}
